/**
 * T036: Updated Onboarding Service for User Story 2
 * 
 * Handles API communication for enhanced onboarding workflow
 * Constitutional compliance: <500ms API responses, enterprise error handling
 */

import { useCallback } from 'react';

// Updated interfaces to match T034-T035 backend API
export interface OnboardingData {
  company_info: {
    company_name: string;
    company_website?: string;
    company_size: string;
    industry: string;
    primary_contact_name: string;
    primary_contact_email: string;
    primary_contact_phone?: string;
    company_description?: string;
  };
  project_details: {
    project_name: string;
    project_description: string;
    project_types: string[];
    priority_level: string;
    budget_range?: string;
    timeline?: string;
    expected_start_date?: string;
    expected_end_date?: string;
  };
  tech_requirements: {
    target_platforms?: string[];
    preferred_technologies?: string[];
    integration_requirements?: string[];
    performance_requirements?: string;
    security_requirements?: string;
    scalability_requirements?: string;
    accessibility_requirements?: string;
  };
  review: {
    terms_accepted: boolean;
    privacy_policy_accepted: boolean;
    communication_preferences?: string[];
    additional_notes?: string;
  };
}

export interface OnboardingSession {
  id: number;
  client_id: number;
  session_key: string;
  status: 'started' | 'in_progress' | 'completed' | 'abandoned' | 'expired' | 'archived';
  current_step: string;
  step_data: Partial<OnboardingData>;
  completion_percentage: number;
  expires_at: string;
  completed_at?: string;
  last_updated_at?: string;
  created_at: string;
  updated_at: string;
  status_label: string;
  current_step_label: string;
  is_expired: boolean;
  is_complete: boolean;
  can_be_resumed: boolean;
  has_project: boolean;
  time_remaining?: string;
  step_progress: {
    current_step: string;
    completed_steps: string[];
    remaining_steps: string[];
    next_step?: string;
    can_navigate_to: string[];
  };
}

export interface OnboardingProgressData {
  current_step: string;
  step_data: Partial<OnboardingData>;
}

export interface OnboardingSubmissionResult {
  onboarding_session: OnboardingSession;
  project?: {
    id: number;
    name: string;
    status: string;
    created_at: string;
  };
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: { [key: string]: string };
  error_id?: string;
}

class OnboardingServiceError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public errorId?: string,
    public validationErrors?: { [key: string]: string }
  ) {
    super(message);
    this.name = 'OnboardingServiceError';
  }
}

export const useOnboardingService = () => {
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  const makeRequest = useCallback(async <T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> => {
    const startTime = performance.now();

    // Get auth token from localStorage or context
    const token = localStorage.getItem('auth_token');

    const response = await fetch(`${baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Request-ID': crypto.randomUUID(),
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });

    const endTime = performance.now();
    const responseTime = endTime - startTime;

    // Performance monitoring for constitutional compliance
    if (responseTime > 500) {
      console.warn(`Slow API response: ${endpoint} took ${responseTime.toFixed(2)}ms`);
    }

    const data: ApiResponse<T> = await response.json();

    if (!response.ok) {
      throw new OnboardingServiceError(
        data.message || 'An error occurred',
        response.status,
        data.error_id,
        data.errors
      );
    }

    if (!data.success) {
      throw new OnboardingServiceError(
        data.message || 'Request failed',
        response.status,
        data.error_id,
        data.errors
      );
    }

    return data.data as T;
  }, [baseUrl]);

  /**
   * Start a new onboarding session (T035: OnboardingController)
   */
  const startOnboarding = useCallback(async (): Promise<OnboardingSession> => {
    try {
      const response = await makeRequest<OnboardingSession>(
        '/onboarding-v2/start',
        { method: 'POST' }
      );

      // Store session key for future requests
      localStorage.setItem('onboarding_session_key', response.session_key);

      console.log('Onboarding session started:', response.session_key);

      return response;
    } catch (error) {
      if (error instanceof OnboardingServiceError) {
        throw error;
      }
      throw new OnboardingServiceError('Failed to start onboarding session');
    }
  }, [makeRequest]);

  /**
   * Get current onboarding session (T035: OnboardingController)
   */
  const getCurrentOnboardingSession = useCallback(async (): Promise<OnboardingSession | null> => {
    try {
      const response = await makeRequest<OnboardingSession>('/onboarding-v2/current');
      
      if (response.session_key) {
        // Store session key for future requests
        localStorage.setItem('onboarding_session_key', response.session_key);
        return response;
      }
      
      return null;
    } catch (error) {
      if (error instanceof OnboardingServiceError && error.statusCode === 404) {
        return null; // No existing session
      }
      if (error instanceof OnboardingServiceError) {
        throw error;
      }
      throw new OnboardingServiceError('Failed to retrieve current onboarding session');
    }
  }, [makeRequest]);

  /**
   * Update step data in onboarding session (T035: OnboardingController)
   */
  const updateOnboardingStep = useCallback(async (
    step: string,
    stepData: any
  ): Promise<OnboardingSession> => {
    try {
      const response = await makeRequest<OnboardingSession>(
        `/onboarding-v2/step/${step}`,
        {
          method: 'PATCH',
          body: JSON.stringify(stepData),
        }
      );

      console.log('Onboarding step updated:', step, response.completion_percentage);
      return response;
    } catch (error) {
      if (error instanceof OnboardingServiceError) {
        throw error;
      }
      throw new OnboardingServiceError('Failed to update onboarding step');
    }
  }, [makeRequest]);

  /**
   * Navigate to specific step (T035: OnboardingController)
   */
  const navigateToStep = useCallback(async (step: string): Promise<OnboardingSession> => {
    try {
      const response = await makeRequest<OnboardingSession>(
        `/onboarding-v2/navigate/${step}`,
        { method: 'POST' }
      );

      console.log('Navigated to step:', step);
      return response;
    } catch (error) {
      if (error instanceof OnboardingServiceError) {
        throw error;
      }
      throw new OnboardingServiceError('Failed to navigate to step');
    }
  }, [makeRequest]);

  /**
   * Complete onboarding session (T035: OnboardingController)
   */
  const completeOnboarding = useCallback(async (createProject: boolean = false): Promise<OnboardingSubmissionResult> => {
    try {
      const result = await makeRequest<OnboardingSubmissionResult>(
        '/onboarding-v2/complete',
        {
          method: 'POST',
          body: JSON.stringify({ create_project: createProject }),
        }
      );

      // Clear session from storage since it's completed
      localStorage.removeItem('onboarding_session_key');

      console.log('Onboarding completed:', result.onboarding_session.session_key);

      return result;
    } catch (error) {
      console.error('Onboarding completion failed:', error);

      if (error instanceof OnboardingServiceError) {
        throw error;
      }
      throw new OnboardingServiceError('Failed to complete onboarding');
    }
  }, [makeRequest]);

  /**
   * Get onboarding session history (T035: OnboardingController)
   */
  const getOnboardingHistory = useCallback(async (page: number = 1, perPage: number = 10): Promise<{
    data: OnboardingSession[];
    meta: any;
  }> => {
    try {
      const response = await makeRequest<{
        data: OnboardingSession[];
        meta: any;
      }>(`/onboarding-v2/history?page=${page}&per_page=${perPage}`);

      return response;
    } catch (error) {
      if (error instanceof OnboardingServiceError) {
        throw error;
      }
      throw new OnboardingServiceError('Failed to retrieve onboarding history');
    }
  }, [makeRequest]);

  /**
   * Resume an onboarding session (T035: OnboardingController)
   */
  const resumeOnboardingSession = useCallback(async (sessionId: number): Promise<OnboardingSession> => {
    try {
      const response = await makeRequest<OnboardingSession>(
        `/onboarding-v2/${sessionId}/resume`,
        { method: 'POST' }
      );

      // Store session key for future requests
      localStorage.setItem('onboarding_session_key', response.session_key);

      console.log('Onboarding session resumed:', response.session_key);
      return response;
    } catch (error) {
      if (error instanceof OnboardingServiceError) {
        throw error;
      }
      throw new OnboardingServiceError('Failed to resume onboarding session');
    }
  }, [makeRequest]);

  /**
   * Auto-save functionality with debouncing
   */
  const autoSaveTimeout = useCallback(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    
    return (step: string, stepData: any, delay: number = 2000) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(async () => {
        try {
          await updateOnboardingStep(step, stepData);
          console.log('Auto-saved onboarding progress');
        } catch (error) {
          console.warn('Auto-save failed:', error);
        }
      }, delay);
    };
  }, [updateOnboardingStep]);

  /**
   * Get current session key
   */
  const getSessionKey = useCallback((): string | null => {
    return localStorage.getItem('onboarding_session_key');
  }, []);

  /**
   * Clear session data
   */
  const clearSession = useCallback((): void => {
    localStorage.removeItem('onboarding_session_key');
  }, []);

  /**
   * Check if there's an active session
   */
  const hasActiveSession = useCallback((): boolean => {
    return !!localStorage.getItem('onboarding_session_key');
  }, []);

  /**
   * Validate step data locally before sending to server
   */
  const validateStepData = useCallback((step: string, data: any): { isValid: boolean; errors: { [key: string]: string } } => {
    const errors: { [key: string]: string } = {};

    switch (step) {
      case 'company_info':
        if (!data.company_name?.trim()) errors.company_name = 'Company name is required';
        if (data.company_name && data.company_name.length < 2) errors.company_name = 'Company name must be at least 2 characters';
        if (!data.company_size) errors.company_size = 'Please select company size';
        if (!data.industry?.trim()) errors.industry = 'Industry is required';
        if (!data.primary_contact_name?.trim()) errors.primary_contact_name = 'Primary contact name is required';
        if (!data.primary_contact_email?.trim()) errors.primary_contact_email = 'Primary contact email is required';
        if (data.primary_contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.primary_contact_email)) {
          errors.primary_contact_email = 'Please enter a valid email address';
        }
        if (data.company_website && !/^https?:\/\/.+/.test(data.company_website)) {
          errors.company_website = 'Please enter a valid website URL';
        }
        break;
        
      case 'project_details':
        if (!data.project_name?.trim()) errors.project_name = 'Project name is required';
        if (data.project_name && data.project_name.length < 3) errors.project_name = 'Project name must be at least 3 characters';
        if (!data.project_description?.trim()) errors.project_description = 'Project description is required';
        if (data.project_description && data.project_description.length < 20) errors.project_description = 'Description must be at least 20 characters';
        if (!data.project_types?.length) errors.project_types = 'Please select at least one project type';
        if (!data.priority_level) errors.priority_level = 'Please select a priority level';
        break;
        
      case 'tech_requirements':
        // Technical requirements are mostly optional, but validate if provided
        if (data.target_platforms && data.target_platforms.length === 0) {
          errors.target_platforms = 'Please select at least one target platform';
        }
        break;
        
      case 'review':
        if (!data.terms_accepted) errors.terms_accepted = 'You must accept the terms and conditions';
        if (!data.privacy_policy_accepted) errors.privacy_policy_accepted = 'You must accept the privacy policy';
        break;
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }, []);

  /**
   * Get available options for form fields (updated for User Story 2)
   */
  const getFormOptions = useCallback(() => {
    return {
      companySizes: [
        { value: 'startup', label: 'Startup (1-10 employees)' },
        { value: 'small', label: 'Small (11-50 employees)' },
        { value: 'medium', label: 'Medium (51-200 employees)' },
        { value: 'large', label: 'Large (201-1000 employees)' },
        { value: 'enterprise', label: 'Enterprise (1000+ employees)' }
      ],
      industries: [
        { value: 'technology', label: 'Technology' },
        { value: 'healthcare', label: 'Healthcare' },
        { value: 'finance', label: 'Finance & Banking' },
        { value: 'education', label: 'Education' },
        { value: 'retail', label: 'Retail & E-commerce' },
        { value: 'manufacturing', label: 'Manufacturing' },
        { value: 'real_estate', label: 'Real Estate' },
        { value: 'consulting', label: 'Consulting' },
        { value: 'nonprofit', label: 'Non-profit' },
        { value: 'other', label: 'Other' }
      ],
      projectTypes: [
        { value: 'web_app', label: 'Web Application' },
        { value: 'mobile_app', label: 'Mobile Application' },
        { value: 'desktop_app', label: 'Desktop Application' },
        { value: 'api', label: 'API/Backend Service' },
        { value: 'website', label: 'Corporate Website' },
        { value: 'ecommerce', label: 'E-commerce Platform' },
        { value: 'crm', label: 'CRM System' },
        { value: 'cms', label: 'Content Management System' },
        { value: 'analytics', label: 'Analytics Dashboard' },
        { value: 'other', label: 'Other' }
      ],
      priorityLevels: [
        { value: 'low', label: 'Low Priority' },
        { value: 'normal', label: 'Normal Priority' },
        { value: 'high', label: 'High Priority' },
        { value: 'urgent', label: 'Urgent' }
      ],
      budgetRanges: [
        { value: 'under_5k', label: 'Under $5,000' },
        { value: '5k_to_15k', label: '$5,000 - $15,000' },
        { value: '15k_to_30k', label: '$15,000 - $30,000' },
        { value: '30k_to_50k', label: '$30,000 - $50,000' },
        { value: '50k_to_100k', label: '$50,000 - $100,000' },
        { value: 'over_100k', label: 'Over $100,000' }
      ],
      timelines: [
        { value: 'under_1_month', label: 'Under 1 month' },
        { value: '1_to_3_months', label: '1-3 months' },
        { value: '3_to_6_months', label: '3-6 months' },
        { value: '6_to_12_months', label: '6-12 months' },
        { value: 'over_12_months', label: 'Over 12 months' }
      ],
      targetPlatforms: [
        { value: 'web', label: 'Web Browser' },
        { value: 'ios', label: 'iOS (iPhone/iPad)' },
        { value: 'android', label: 'Android' },
        { value: 'windows', label: 'Windows Desktop' },
        { value: 'macos', label: 'macOS Desktop' },
        { value: 'linux', label: 'Linux Desktop' }
      ],
      technologies: [
        { value: 'react', label: 'React' },
        { value: 'vue', label: 'Vue.js' },
        { value: 'angular', label: 'Angular' },
        { value: 'nodejs', label: 'Node.js' },
        { value: 'python', label: 'Python' },
        { value: 'java', label: 'Java' },
        { value: 'dotnet', label: '.NET' },
        { value: 'php', label: 'PHP' },
        { value: 'laravel', label: 'Laravel' },
        { value: 'postgresql', label: 'PostgreSQL' },
        { value: 'mysql', label: 'MySQL' },
        { value: 'mongodb', label: 'MongoDB' },
        { value: 'aws', label: 'AWS' },
        { value: 'azure', label: 'Microsoft Azure' },
        { value: 'gcp', label: 'Google Cloud' }
      ],
      communicationPreferences: [
        { value: 'email', label: 'Email Updates' },
        { value: 'phone', label: 'Phone Calls' },
        { value: 'slack', label: 'Slack/Teams' },
        { value: 'weekly_reports', label: 'Weekly Reports' },
        { value: 'milestone_updates', label: 'Milestone Updates' }
      ]
    };
  }, []);

  return {
    // Core session management
    startOnboarding,
    getCurrentOnboardingSession,
    updateOnboardingStep,
    navigateToStep,
    completeOnboarding,
    
    // Session utilities
    getOnboardingHistory,
    resumeOnboardingSession,
    autoSave: autoSaveTimeout(),
    getSessionKey,
    clearSession,
    hasActiveSession,
    
    // Validation and options
    validateStepData,
    getFormOptions,
    OnboardingServiceError
  };
};

export default useOnboardingService;